<div class="container-fluid">
    <div class="d-flex align-items-center mb-3 pt-3">
        <h3 class="mb-0">Shapefile importeren</h3>
    </div>
    @if (errorMessage) {
        <div class="alert alert-danger">{{ errorMessage }}</div>
    }

    <form [formGroup]="importForm">
        <div class="card mb-3">
            <div class="card-body">
                <h5 class="card-title">Opdrachtgever &amp; project</h5>

                <div class="mb-3">
                    <label>Opdrachtgever</label>
                    <ng-select
                        [items]="customers"
                        bindLabel="name"
                        bindValue="id"
                        placeholder="Selecteer opdrachtgever..."
                        formControlName="customer_id">
                    </ng-select>
                    <mat-error [control]="importForm.get('customer_id')"></mat-error>
                </div>
                @if (importForm.get('customer_id').value) {
                    <div class="mb-3">
                        <label>Project</label>
                        <ng-select
                            [items]="projects"
                            bindLabel="name"
                            bindValue="id"
                            placeholder="Selecteer project..."
                            formControlName="project_id">
                        </ng-select>
                        <mat-error [control]="importForm.get('project_id')"></mat-error>
                    </div>
                }
            </div>
        </div>

        <div class="card mb-3">
            <div class="card-body">
                <h5 class="card-title">Shapefile (ZIP)</h5>
                <p class="text-muted small">
                    Upload een ZIP-bestand met daarin één of meer .shp bestanden (inclusief .dbf).
                </p>
                <div class="mb-3">
                    <input type="file" accept=".zip" class="form-control" (change)="onFileSelected($event)" />
                </div>
            </div>
        </div>

        @if (shapefileCombinations.length > 0) {
            <div class="card mb-3">
                <div class="card-body">
                    <h5 class="card-title">Shapefile combinaties</h5>

                    <div class="mb-2">
                        <button type="button" mat-stroked-button class="me-2" (click)="selectAllCombinations()">
                            Alles selecteren
                        </button>
                        <button type="button" mat-stroked-button (click)="deselectAllCombinations()">
                            Alles deselecteren
                        </button>
                    </div>

                    <mat-selection-list>
                        @for (combo of shapefileCombinations; track combo.name) {
                            <mat-list-option
                                [selected]="isCombinationSelected(combo)"
                                (selectedChange)="onSelectionChange(combo, $event)">
                                {{ combo.name }}
                            </mat-list-option>
                        }
                    </mat-selection-list>

                    <button
                        type="button"
                        mat-raised-button
                        color="primary"
                        class="mt-2"
                        [disabled]="selectedCombinations.length === 0"
                        (click)="processSelectedCombinations()">
                        Verwerk geselecteerde combinaties
                    </button>
                    @if (allFeatures.length > 0) {
                        <button type="button" mat-stroked-button class="mt-2 ms-2" (click)="openFeaturePreviewDialog()">
                            <i class="fal fa-table me-1"></i>
                            Bekijk data ({{ allFeatures.length }} features)
                        </button>
                    }
                </div>
            </div>
        }

        @if (featuresProcessed) {
            <div class="alert alert-info">
                {{ allFeatures.length }} features geladen uit {{ selectedCombinations.length }} shapefile(s). Kolommen:
                {{ shapefileColumns.join(', ') }}
            </div>
            <div class="card mb-3">
                <div class="card-body">
                    <h5 class="card-title">Opties</h5>
                    <mat-checkbox formControlName="autoFillLocationFields">
                        Locatievelden automatisch ophalen (straat, postcode, plaats) via coördinaten
                    </mat-checkbox>
                </div>
            </div>
            <div class="card mb-3">
                <div class="card-body">
                    <h5 class="card-title">Werkvak koppeling</h5>
                    <p class="text-muted small">Koppel shapefile-kolommen aan de basisvelden van een werkvak.</p>

                    <div formArrayName="workareaMappings">
                        @for (field of workareaFields; track field; let i = $index) {
                            <div class="mb-3 row align-items-center">
                                <label class="col-sm-6 col-form-label">
                                    <strong>{{ workareaFieldMeta[field].label }}</strong>
                                    @if (workareaFieldMeta[field].description) {
                                        <small class="d-block text-muted">{{
                                            workareaFieldMeta[field].description
                                        }}</small>
                                    }
                                </label>
                                <div class="col-sm-18">
                                    <ng-select
                                        [items]="shapefileColumns"
                                        placeholder="Geen koppeling"
                                        [clearable]="true"
                                        [formControlName]="i">
                                    </ng-select>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>

            @if (fieldOptionsLoaded) {
                <div class="card mb-3">
                    <div class="card-body">
                        <h5 class="card-title">Extra velden koppeling</h5>
                        <p class="text-muted small">
                            Koppel shapefile-kolommen aan de extra domeinvelden. Per kolom kunt u de waarden vertalen.
                        </p>

                        <div formArrayName="extraMappings">
                            @for (extraKey of extraFieldKeys; track extraKey; let i = $index) {
                                <div class="mb-4">
                                    <div class="mb-3 row align-items-center">
                                        <label class="col-sm-6 col-form-label">
                                            <strong>{{ extraFieldLabels[extraKey] }}</strong>
                                        </label>
                                        <div class="col-sm-18">
                                            <ng-select
                                                [items]="shapefileColumns"
                                                placeholder="Geen koppeling"
                                                [clearable]="true"
                                                [formControlName]="i"
                                                (change)="onExtraColumnSelected(extraKey, i, $event)">
                                            </ng-select>
                                        </div>
                                    </div>

                                    <!-- Value mappings for this extra field -->
                                    @if (extraFieldUniqueValues.get(extraKey)?.length > 0) {
                                        <div class="ps-3 ps-sm-5">
                                            <p class="text-muted small mb-1">
                                                Waardekoppeling voor <em>{{ extraFieldLabels[extraKey] }}</em>
                                                :
                                            </p>
                                            @for (shpVal of extraFieldUniqueValues.get(extraKey); track shpVal) {
                                                <div class="row align-items-center mb-1">
                                                    <label class="col-sm-8 col-form-label small fst-italic">{{
                                                        shpVal
                                                    }}</label>
                                                    <div class="col-sm-16">
                                                        <ng-select
                                                            [items]="extraFieldOptions[extraKey]"
                                                            placeholder="Niet koppelen"
                                                            [clearable]="true"
                                                            [ngModel]="extraFieldMappings.get(extraKey)?.get(shpVal)"
                                                            [ngModelOptions]="{ standalone: true }"
                                                            (change)="setExtraValueMapping(extraKey, shpVal, $event)">
                                                        </ng-select>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
            }

            <div class="card mb-3">
                <div class="card-body">
                    <h5 class="card-title">Velden opnemen in opmerkingen</h5>
                    <p class="text-muted small">
                        Selecteer shapefile-kolommen waarvan de naam en waarde worden toegevoegd aan het opmerkingenveld
                        van elk werkvak.<br />
                        <em>
                            Formaat: bestaande opmerking (indien aanwezig), gevolgd door een lege regel, dan per
                            geselecteerde kolom <code>KOLOMNAAM: waarde</code>.
                        </em>
                    </p>
                    <ng-select
                        [items]="shapefileColumns"
                        formControlName="commentFields"
                        [multiple]="true"
                        [closeOnSelect]="false"
                        placeholder="Geen kolommen geselecteerd"
                        [clearable]="true">
                    </ng-select>
                </div>
            </div>

            <div class="card mb-4">
                <div class="card-body">
                    @if (saving) {
                        <div class="mb-2">
                            <mat-progress-bar
                                mode="determinate"
                                [value]="saveTotal > 0 ? (saveCount / saveTotal) * 100 : 0">
                            </mat-progress-bar>
                            <small class="text-muted">{{ saveCount }} / {{ saveTotal }} werkvakken opgeslagen…</small>
                        </div>
                    }
                    <button
                        type="button"
                        mat-raised-button
                        color="primary"
                        [disabled]="saving || importForm.invalid"
                        (click)="importWorkareas()">
                        <mat-icon class="fas fa-upload me-1"></mat-icon>
                        Importeren
                        @if (saving) {
                            <span>({{ saveCount }}/{{ saveTotal }})</span>
                        }
                    </button>
                </div>
            </div>
        }
    </form>
</div>
